import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Creditcards } from 'creditcards-info';
import CurrencyUtils from '@wix/wixrest-utils/dist/CurrencyUtils';
import X from 'wix-ui-icons-common/X';
import PrettyIcon from '../PrettyIcon';
import PaymentMethodIcon, { ICON_TYPES } from '../PayManually/PaymentMethodIcon';
import Tip from './Tip';
import styles from './PaymentLine.scss';
import PaymentLineDishes from './PaymentLineDishes';
import { CurrencyInput } from './CurrencyInput';
import _ from 'lodash';
import InfoCircle from 'wix-ui-icons-common/InfoCircle';
import { translate } from 'react-i18next';
import ExternalPaymentMethodIcon from '../PayManually/ExternalPaymentMethodIcon';

import { Text, Popover } from 'wix-style-react';

const PAYMENT_TYPES = {
  CREDIT: 'credit',
  CIBUS: 'il.co.mysodexo',
  CASH: 'cash',
  CASHIER: 'cashier',
};

class PaymentLine extends Component {
  state = {
    isPopoverVisible: false,
  };

  inputRef = React.createRef();

  componentDidMount() {
    if (this.props.displayAmountAsLabel) return;
    this.inputRef.current.focus();
  }

  getPaymentInfo = (type, t) => {
    switch (type) {
      case PAYMENT_TYPES.CREDIT:
        const { paymentValue, locale } = this.props;
        const creditCard = _.get(paymentValue, 'cardToken.creditCard', {});
        const network = creditCard.network || ICON_TYPES.CREDIT;
        const holderName = _.get(creditCard, 'additionalFields.holderName', this.getOfflineCardText());
        const lastDigits = creditCard.lastDigits;
        const digitsText = `${Creditcards.getCreditcardName(locale, network)} ending in ${lastDigits}`;

        return {
          icon: ICON_TYPES[network.toUpperCase()],
          title: holderName,
          description: lastDigits ? digitsText : this.renderSupportedNetworks(),
          allowTip: this.props.acceptsTip,
        };
      case PAYMENT_TYPES.CASH:
        return {
          icon: ICON_TYPES.CASH,
          title: t('phoneordering_checkout_cash_label'),
          description: null,
          allowTip: false,
        };
      case PAYMENT_TYPES.CIBUS:
        const { card } = this.props.paymentValue;
        return {
          icon: ICON_TYPES.CIBUS,
          title: card.holderName,
          description: `Number: ${card.number}`,
          allowTip: false,
        };
      case PAYMENT_TYPES.CASHIER:
        const { paymentMethod, paymentMethodTitle } = this.props.paymentValue;
        const { paymentMethods } = this.props;
        const { methodIcon } = paymentMethods.find(pm => pm.paymentMethod === paymentMethod);
        return {
          title: paymentMethodTitle,
          allowTip: this.props.acceptsTip && paymentMethod !== 'offline',
          svgUrl: methodIcon,
        };
      default:
        return null;
    }
  };

  onTipChange = tip => {
    this.props.onChange({
      ...this.props.paymentValue,
      tip,
    });
  };

  onAmountChange = amount => {
    this.props.onChange({
      ...this.props.paymentValue,
      manualAmount: amount,
    });
  };

  handleRemoveTipClick = () => {
    this.props.onChange({
      ...this.props.paymentValue,
      tip: 0,
    });
  };

  getOfflineCardText = () => {
    const { t } = this.props;
    const { collectionMethod } = this.props.paymentValue;
    if (collectionMethod === 'phone') {
      return t('phoneordering_checkout_paymentmethod_card_title');
    } else if (collectionMethod === 'cod') {
      return t('phoneordering_checkout_paymentmethod_card_delivery_title');
    }
  };

  renderSupportedNetworks = () => {
    const { t } = this.props;

    const tooltipContent = (
      <div style={{ maxWidth: 208, padding: 12, paddingBottom: 6 }}>
        {this.props.networks.map((network, index) => {
          const type = ICON_TYPES[network.toUpperCase()];
          return (
            type && (
              <div key={network} style={{ height: 30, overflow: 'hidden', display: 'inline-block' }}>
                <PaymentMethodIcon
                  type={type}
                  size={40}
                  style={{ marginRight: (index + 1) % 4 === 0 ? 0 : 8, display: 'inline-block', marginTop: -5 }}
                />
              </div>
            )
          );
        })}
      </div>
    );

    return (
      <div className={styles.supportedNetworks}>
        {t('phoneordering_checkout_paymentmethod_card_description')}
        <Popover
          shown={this.state.isPopoverVisible}
          onMouseEnter={() => this.setState({ isPopoverVisible: true })}
          onMouseLeave={() => this.setState({ isPopoverVisible: false })}
          showArrow
          appendTo="window"
          placement="top"
        >
          <Popover.Element>
            <InfoCircle size="20px" className={styles.supportedNetworksIcon} />
          </Popover.Element>
          <Popover.Content>{tooltipContent}</Popover.Content>
        </Popover>
      </div>
    );
  };

  render() {
    const {
      paymentValue: { manualAmount = '', tip, type, ppdAmount = '' },
      currencySymbol,
      currency,
      onRemove,
      dataHook,
      displayAmountAsLabel,
      locale,
      onClick,
      orderItems,
      priceComponents,
      displayableDiscounts,
      payPerDish,
      t,
    } = this.props;

    const paymentInfo = this.getPaymentInfo(type, t);

    const prefix = (
      <Text size="small" weight="normal" className={styles.prefix}>
        {currencySymbol}
      </Text>
    );

    const icon = paymentInfo.svgUrl ? (
      <ExternalPaymentMethodIcon svgUrl={paymentInfo.svgUrl} alt={paymentInfo.title} />
    ) : (
      <PaymentMethodIcon type={paymentInfo.icon} />
    );

    return (
      <div className={styles.root} data-hook={dataHook}>
        <div className={styles.paymentRoot}>
          <div className={styles.paymentGroup}>
            {icon}
            <div className={styles.paymentName}>
              <Text size="medium" weight="normal" data-hook="payment-line-name">
                {paymentInfo.title}
              </Text>
              {paymentInfo.description && (
                <Text size="small" weight="normal" light secondary data-hook="payment-line-description">
                  {paymentInfo.description}{' '}
                </Text>
              )}
            </div>
          </div>
          <div className={styles.paymentGroup}>
            {displayAmountAsLabel ? (
              <Text size="medium" weight="normal" data-hook="payment-line-amount-label">
                {CurrencyUtils.toStr((payPerDish ? ppdAmount : manualAmount || 0) / 100, currency, locale)}
              </Text>
            ) : (
              <CurrencyInput
                data-hook="payment-line-currency-input"
                innerRef={this.inputRef}
                locale={locale}
                currency={currency}
                prefix={prefix}
                value={manualAmount}
                onChange={this.onAmountChange}
              />
            )}
            <PrettyIcon onClick={onRemove} className={styles.closeButton} data-hook="payment-line-remove-button">
              <X />
            </PrettyIcon>
          </div>
        </div>
        {payPerDish && (
          <PaymentLineDishes
            onSelectDishesClick={onClick}
            orderItems={orderItems}
            priceComponents={priceComponents}
            displayableDiscounts={displayableDiscounts}
            locale={locale}
            currency={currency}
          />
        )}
        {paymentInfo.allowTip && (
          <Tip
            onChange={this.onTipChange}
            onRemove={this.handleRemoveTipClick}
            tip={tip}
            currency={currency}
            locale={locale}
          />
        )}
      </div>
    );
  }
}

PaymentLine.propTypes = {
  paymentValue: PropTypes.shape({
    amount: PropTypes.number,
    tip: PropTypes.number,
    type: PropTypes.oneOf(Object.values(PAYMENT_TYPES)).isRequired,
    cardToken: PropTypes.shape({
      token: PropTypes.string,
      creditCard: PropTypes.shape({
        lastDigits: PropTypes.string,
        expiration: PropTypes.shape({
          year: PropTypes.number,
          month: PropTypes.number,
        }),
        network: PropTypes.oneOf(Object.values(ICON_TYPES)),
        additionalFields: PropTypes.shape({
          holderId: PropTypes.string,
          holderName: PropTypes.string,
          billingAddress: PropTypes.string,
          billingPostalCode: PropTypes.string,
          billingAddressDetailed: PropTypes.string,
        }),
      }),
    }),
    collectionMethod: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func,
  currencySymbol: PropTypes.string,
  currency: PropTypes.string,
  locale: PropTypes.string.isRequired,
  dataHook: PropTypes.string,
  displayAmountAsLabel: PropTypes.bool,
  payPerDish: PropTypes.bool,
  onClick: PropTypes.func,
  orderItems: PropTypes.array,
  priceComponents: PropTypes.object,
  displayableDiscounts: PropTypes.array,
  acceptsTip: PropTypes.bool,
  networks: PropTypes.array,
  paymentMethods: PropTypes.array,
};

PaymentLine.defaultProps = {
  currencySymbol: '$',
  acceptsTip: false,
  displayPriceAsLabel: false,
  priceComponents: {},
  displayableDiscounts: [],
  networks: [],
  paymentMethods: [],
};

export default translate()(PaymentLine);
